import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { Capacitor } from '@capacitor/core';

/**
 * Base URL strategy:
 *  - web dev / web prod behind reverse proxy: '' (same origin, Vite/nginx proxies)
 *  - iOS/Android native shell: absolute origin from VITE_API_URL
 */
export const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? (Capacitor.isNativePlatform() ? 'http://localhost:4000' : '');

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'content-type': 'application/json' },
});

/** Set by the auth store so the interceptor has no circular import. */
export const tokenBridge: { get: () => string | null; refresh: () => Promise<boolean>; onUnauthorized: () => void } = {
  get: () => null,
  refresh: async () => false,
  onUnauthorized: () => undefined,
};

api.interceptors.request.use((config) => {
  const token = tokenBridge.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing: Promise<boolean> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retried && !original.url?.includes('/auth/')) {
      original._retried = true;
      refreshing = refreshing ?? tokenBridge.refresh().finally(() => (refreshing = null));
      const ok = await refreshing;
      if (ok) return api.request(original);
      tokenBridge.onUnauthorized();
    }
    return Promise.reject(error);
  }
);

/** Human-readable message from any API error for toasts. */
export function apiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as {
      error?: string;
      message?: string;
      details?: {
        variants?: Array<{ platform?: string; errors?: string[] }>;
      };
    } | undefined;
    const base = data?.error ?? data?.message ?? error.message;
    if (data?.details?.variants?.length) {
      const parts = data.details.variants
        .map((v) => {
          const platform = v.platform ? `${v.platform}: ` : '';
          return `${platform}${v.errors?.join('; ') ?? ''}`;
        })
        .join(' | ');
      return `${base}. ${parts}`;
    }
    return base;
  }
  return error instanceof Error ? error.message : 'Unexpected error';
}

export interface Paginated<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}
